import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ticket } from "lucide-react";

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  FormField,
  H3,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@lagoon/ui";

import { api, ApiError, type CreatedPass, type GuestPass } from "../api.js";
import { LoadingPanel, Notice } from "./feedback.js";

/**
 * Guest links.
 *
 * The allowlist answers "who may always see this site", which is the wrong
 * tool for showing a client a page for a day: adding somebody permanently and
 * remembering to remove them is a manual expiry nobody performs.
 *
 * The URL is shown once, because it is stored hashed and this is the only
 * moment it exists in a usable form. A lost link is replaced, not recovered,
 * and the UI has to say so rather than let someone close the panel expecting
 * to find it again.
 *
 * Revoking used to be `window.prompt`, which took a reason and discarded a
 * short one in silence. It is a `ConfirmDialog` now: the same reason, asked for
 * in a labelled field, with the rule stated where it can be read.
 */
export function Passes(props: { siteId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState("");
  const [hours, setHours] = useState(24);
  const [limitUses, setLimitUses] = useState(false);
  const [maxUses, setMaxUses] = useState(1);
  const [created, setCreated] = useState<CreatedPass | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState<GuestPass | null>(null);

  const passes = useQuery({
    queryKey: ["passes", props.siteId],
    queryFn: async () => (await api.listPasses(props.siteId)).passes,
  });

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ["passes", props.siteId] });

  const create = useMutation({
    mutationFn: () =>
      api.createPass(props.siteId, {
        label: label.trim(),
        expiresInHours: hours,
        maxUses: limitUses ? maxUses : null,
      }),
    onSuccess: async (pass) => {
      setCreated(pass);
      setCopied(false);
      setLabel("");
      setError(null);
      await invalidate();
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : "Could not create the link.");
    },
  });

  const revoke = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      api.revokePass(props.siteId, input.id, input.reason),
    onSuccess: async () => {
      setRevoking(null);
      await invalidate();
    },
    onError: (caught) => {
      setRevoking(null);
      setError(caught instanceof ApiError ? caught.message : "Could not revoke the link.");
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    create.mutate();
  }

  return (
    <section className="lg:flex lg:flex-col lg:gap-4">
      <div>
        <H3>Guest links</H3>
        <Text size="sm" tone="muted" className="lg:mt-1">
          A link that works until it expires, for someone who should see this once. They need no
          account and never join the list above.
        </Text>
      </div>

      {error === null ? null : <Notice tone="danger">{error}</Notice>}

      {created === null ? null : (
        <Alert tone="success">
          <AlertTitle>Copy this link now</AlertTitle>
          <AlertDescription className="lg:flex lg:flex-col lg:gap-3">
            <span>
              It is stored encrypted, so this is the only time it can be shown. If you lose it,
              revoke it and make another.
            </span>
            <span className="lg:flex lg:flex-wrap lg:gap-3">
              <Input
                className="lg:min-w-56 lg:flex-1 lg:font-mono lg:text-caption"
                readOnly
                value={created.url}
                aria-label="Guest link"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  void navigator.clipboard.writeText(created.url).then(() => setCopied(true));
                }}
              >
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCreated(null);
                }}
              >
                Done
              </Button>
            </span>
          </AlertDescription>
        </Alert>
      )}

      <form className="lg:flex lg:flex-col lg:gap-4" onSubmit={submit}>
        <div className="lg:flex lg:flex-wrap lg:items-end lg:gap-3">
          <Input
            className="lg:min-w-56 lg:flex-1"
            value={label}
            onChange={(event) => {
              setLabel(event.target.value);
            }}
            placeholder="What is this link for? e.g. Acme board review"
            required
            maxLength={200}
            aria-label="What this guest link is for"
          />
          <Select
            value={String(hours)}
            onValueChange={(value) => {
              setHours(Number(value));
            }}
          >
            <SelectTrigger aria-label="How long the link lasts" className="lg:w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 hour</SelectItem>
              <SelectItem value="24">1 day</SelectItem>
              <SelectItem value={String(24 * 7)}>1 week</SelectItem>
              <SelectItem value={String(24 * 30)}>30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" type="submit" disabled={create.isPending || !label.trim()}>
            {create.isPending ? "Creating…" : "Create link"}
          </Button>
        </div>

        <div className="lg:flex lg:flex-wrap lg:items-center lg:gap-3">
          <Checkbox
            id="pass-limit-uses"
            checked={limitUses}
            onCheckedChange={(next) => {
              setLimitUses(next === true);
            }}
          />
          {/* The sentence voice, not the tiny mono field-label voice: this
              labels a choice, not a field in a form column. */}
          <Label
            htmlFor="pass-limit-uses"
            className="lg:font-sans lg:text-body-sm lg:normal-case lg:tracking-normal lg:text-foreground"
          >
            Limit how many times it can be used
          </Label>
          {limitUses ? (
            <Input
              type="number"
              inputSize="sm"
              min={1}
              max={100}
              value={maxUses}
              onChange={(event) => {
                setMaxUses(Number(event.target.value));
              }}
              className="lg:w-24"
              aria-label="Maximum uses"
            />
          ) : null}
        </div>
      </form>

      {passes.isPending ? (
        <LoadingPanel rows={2} label="Loading guest links" />
      ) : passes.data && passes.data.length > 0 ? (
        <ul className="lg:flex lg:flex-col lg:gap-px lg:overflow-hidden lg:rounded-lg lg:border lg:border-hairline lg:bg-hairline">
          {passes.data.map((pass) => (
            <li
              key={pass.id}
              className="lg:flex lg:flex-wrap lg:items-start lg:gap-4 lg:bg-card lg:px-5 lg:py-4"
            >
              <div className="lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-1.5">
                <span className="lg:flex lg:flex-wrap lg:items-center lg:gap-2">
                  <span className="lg:font-medium lg:text-strong">{pass.label}</span>
                  <Badge tone={pass.active ? "success" : "neutral"}>
                    {pass.active ? "working" : pass.revokedAt ? "revoked" : "expired"}
                  </Badge>
                </span>
                <Text as="span" size="caption" tone="subtle">
                  used {pass.uses}
                  {pass.maxUses === null ? "" : ` of ${pass.maxUses}`} ·{" "}
                  {pass.active
                    ? `expires ${new Date(pass.expiresAt).toLocaleString()}`
                    : `expired ${new Date(pass.expiresAt).toLocaleDateString()}`}
                </Text>
              </div>
              {pass.active ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={revoke.isPending}
                  onClick={() => {
                    setRevoking(pass);
                  }}
                >
                  Revoke
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          glow={false}
          icon={<Ticket />}
          title="No guest links yet"
          description="Create one above to show somebody this site without adding them to the list."
        />
      )}

      {revoking === null ? null : (
        <RevokePass
          pass={revoking}
          pending={revoke.isPending}
          onCancel={() => {
            setRevoking(null);
          }}
          onConfirm={(reason) => {
            revoke.mutate({ id: revoking.id, reason });
          }}
        />
      )}
    </section>
  );
}

/**
 * Revoking a guest link, and asking why.
 *
 * The reason goes into the audit trail, so the minimum length is a real rule
 * rather than validation for its own sake. It is enforced here by keeping the
 * dialog open and naming the rule, which is what the old `window.prompt` could
 * not do: cancelling and typing two characters were indistinguishable, and both
 * left the button looking broken.
 */
function RevokePass(props: {
  pass: GuestPass;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}): React.ReactElement {
  const [reason, setReason] = useState("no longer needed");
  const [touched, setTouched] = useState(false);
  const tooShort = reason.trim().length < 3;

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) props.onCancel();
      }}
      title={`Revoke "${props.pass.label}"?`}
      description="Anyone using it right now loses access immediately, not when it would have expired."
      confirmLabel={props.pending ? "Revoking…" : "Revoke link"}
      destructive
      pending={props.pending}
      onConfirm={() => {
        setTouched(true);
        if (!tooShort) props.onConfirm(reason.trim());
      }}
    >
      <FormField
        label="Why?"
        hint="Recorded in the audit trail, so it needs at least 3 characters."
        {...(touched && tooShort ? { error: "A few more characters, please." } : {})}
      >
        <Input
          value={reason}
          autoFocus
          maxLength={200}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </FormField>
    </ConfirmDialog>
  );
}
